import { useMemo } from 'react';

import {
  getOrderServiceFeeDescriptions,
  getOrderSaleItems,
  getOrderCouponCode,
  getOrderProductCount,
  getOrderRepresentativeCoupon,
  getOrderSubtotalCents,
  getOrderTotalAmountCents,
  type Order,
} from '@src/domains/orders';
import { getCouponAffectedProductNames, shouldShowCouponAffectedProducts } from '@src/domains/coupons';
import { shouldShowServiceFee } from '@src/domains/service-fees';
import {
  getOrderCouponDiscountCents,
  getOrderCouponPercentLabel,
  getOrderDiscountCents,
} from '@src/features/orders/summary/core/orderCouponSummary';
import { orderSummaryHasDetails } from '@src/features/orders/summary/core/orderSummaryHasDetails';
import useFeatureFlag from '@src/hooks/useFeatureFlag';

export type UseOrderSummaryResult = {
  productCount: number;
  subtotalCents: number;
  totalAmountCents: number;
  discountCents: number;
  couponCode?: string;
  couponDiscountCents: number;
  couponPercentLabel?: string;
  affectedProductNames: string[];
  showAffectedProducts: boolean;
  showServiceFee: boolean;
  serviceFeeAmountCents: number;
  serviceFeeDescription?: string;
  hasDetails: boolean;
  items: ReturnType<typeof getOrderSaleItems>;
};

export const useOrderSummary = (order: Order | null | undefined): UseOrderSummaryResult => {
  const { isFeatureFlagEnabled } = useFeatureFlag();
  const serviceFeeFeatureEnabled = isFeatureFlagEnabled('PR-647-service-fee');

  const totalDiscountCents = getOrderDiscountCents(order);
  const couponDiscountCents = getOrderCouponDiscountCents(order);
  const showServiceFee = shouldShowServiceFee(order?.serviceFeeCurrencyInfo?.amountCents, serviceFeeFeatureEnabled);
  const serviceFeeAmountCents = order?.serviceFeeCurrencyInfo?.amountCents ?? 0;
  const serviceFeeDescription = useMemo(() => getOrderServiceFeeDescriptions(order).join(', ') || undefined, [order]);
  const items = useMemo(() => (order ? getOrderSaleItems(order) : []), [order]);
  const coupon = getOrderRepresentativeCoupon(order);
  const affectedProductNames = getCouponAffectedProductNames(coupon, items);

  return {
    productCount: getOrderProductCount(order),
    subtotalCents: getOrderSubtotalCents(order),
    totalAmountCents: getOrderTotalAmountCents(order),
    discountCents: totalDiscountCents,
    couponCode: getOrderCouponCode(order),
    couponDiscountCents,
    couponPercentLabel: getOrderCouponPercentLabel(order),
    affectedProductNames,
    showAffectedProducts: shouldShowCouponAffectedProducts(coupon, items),
    showServiceFee,
    serviceFeeAmountCents,
    serviceFeeDescription,
    hasDetails: orderSummaryHasDetails(order, serviceFeeFeatureEnabled),
    items,
  };
};

export default useOrderSummary;
